import { Badge, Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui'
import { Lock, Clock, Info, AlertTriangle } from 'lucide-react'
import { formatDateTime } from '@/lib/timeFormatters'
import type { WorkflowRunApprovalRequest } from '@/types'

interface Props {
  approvalRequest?: WorkflowRunApprovalRequest
  status: 'Pending' | 'Approved' | 'Rejected' | 'AwaitingApproval'
}

export function ApprovalGateBadge({ approvalRequest, status }: Props) {
  if (status === 'AwaitingApproval' || (status === 'Pending' && !approvalRequest)) {
    return (
      <div className="rounded-lg border border-warning-foreground bg-warning px-3 py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-neutral-foreground">
            <AlertTriangle className="h-4 w-4 text-warning-foreground" />
            Approval Gate
          </div>
          <Badge variant="outline" className="gap-1 text-xs bg-input-background text-warning border-neutral-foreground">
            <Clock className="h-3 w-3 animate-pulse" /> Awaiting Approval
          </Badge>
        </div>
      </div>
    )
  }

  if (approvalRequest?.status === 'Approved') {
    return (
      <div className="rounded-lg border border-success-foreground bg-success px-3 py-3 mb-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-neutral-foreground">
            <AlertTriangle className="h-4 w-4 text-neutral-foreground" />
            Approval Gate
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="outline" className="gap-1 text-xs bg-background text-neutral-foreground border-neutral-foreground cursor-help">
                  Approved Gate
                  <Info className="h-3 w-3 ml-0.5" />
                </Badge>
              </TooltipTrigger>
              <TooltipContent side="right" className="text-xs space-y-1 max-w-64">
                <p><span className="font-medium">Approved by:</span> {approvalRequest.decidedBy}</p>
                <p><span className="font-medium">Date:</span> {formatDateTime(approvalRequest.decidedAt)}</p>
                {approvalRequest.comment && (
                  <p><span className="font-medium">Comment:</span> {approvalRequest.comment}</p>
                )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    )
  }

  if (approvalRequest?.status === 'Rejected') {
    return (
      <div className="rounded-lg border border-danger-foreground bg-danger px-3 py-3 mb-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-neutral-foreground">
            <AlertTriangle className="h-4 w-4 text-neutral-foreground" />
            Approval Gate
          </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="outline" className="gap-1 text-xs bg-background text-neutral-foreground border-neutral-foreground cursor-help">
                <Lock className="h-3 w-3" /> Rejected Gate
                <Info className="h-3 w-3 ml-0.5" />
              </Badge>
            </TooltipTrigger>
            <TooltipContent side="right" className="text-xs space-y-1 max-w-64">
              <p><span className="font-medium">Rejected by:</span> {approvalRequest.decidedBy}</p>
              <p><span className="font-medium">Date:</span> {formatDateTime(approvalRequest.decidedAt)}</p>
              {approvalRequest.comment && (
                <p><span className="font-medium">Reason:</span> {approvalRequest.comment}</p>
              )}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        </div>
      </div>
    )
  }

  return null
}
